import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Smartphone } from "lucide-react";

/**
 * AppDownloadBanner — a subtle, dismissible prompt to grab the phone app.
 *
 *  • Desktop (lg+): a small glass card bottom-left with a scannable QR
 *    (→ /download) so a visitor can scan with their phone.
 *  • Mobile (< lg): a slim pill bottom-center that links straight to
 *    the /download page (a QR is useless on the phone you're holding).
 *
 * Dismissal is remembered in localStorage so it never nags twice.
 */
const KEY = "worthify_app_banner_dismissed";

export const AppDownloadBanner = ({ t }) => {
  const tr = (k, fb) => {
    const v = t ? t(k) : null;
    return v && v !== k ? v : fb;
  };
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Never show inside the native app shell (WebView) — it's already the app.
    const qp = (() => { try { return new URLSearchParams(window.location.search).get("worthify_app") === "1"; } catch { return false; } })();
    if (qp) { try { localStorage.setItem("worthify_in_app", "1"); } catch { /* ignore */ } }
    const inApp =
      typeof window !== "undefined" &&
      (window.__WORTHIFY_APP__ === true ||
        qp ||
        (() => { try { return localStorage.getItem("worthify_in_app") === "1"; } catch { return false; } })() ||
        (typeof navigator !== "undefined" && (navigator.userAgent || "").includes("WorthifyApp")));
    if (inApp) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(KEY) === "1"; } catch { /* ignore */ }
    if (!dismissed) {
      const timer = setTimeout(() => setShow(true), 1800); // gentle delayed entrance
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Desktop — QR card */}
          <motion.div
            key="app-banner-desktop"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            className="hidden lg:flex fixed bottom-6 left-6 z-[60] items-center gap-3"
            style={{
              padding: "12px 14px",
              borderRadius: 18,
              background: "rgba(11,18,32,0.82)",
              border: "1px solid rgba(52,211,153,0.22)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              boxShadow: "0 18px 44px -12px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)",
              maxWidth: 288,
            }}
            data-testid="app-download-banner-desktop"
          >
            <Link to="/download" aria-label={tr("downloadBannerTitle", "Get the Worthify app")}>
              <img
                src="/qr-download.png"
                alt="QR — download the Worthify app"
                width={74}
                height={74}
                style={{ display: "block", borderRadius: 10, background: "#fff", padding: 4 }}
                draggable={false}
              />
            </Link>
            <div style={{ minWidth: 0 }}>
              <div className="flex items-center gap-1.5">
                <Smartphone size={14} style={{ color: "#34D399" }} strokeWidth={2.4} />
                <span style={{ fontSize: 12.5, fontWeight: 800, color: "#F8FAFC", letterSpacing: "-0.2px" }}>
                  {tr("downloadBannerTitle", "Get the app")}
                </span>
              </div>
              <p style={{ fontSize: 11.5, color: "#94A3B8", lineHeight: 1.4, marginTop: 3 }}>
                {tr("downloadBannerScan", "Scan to install on your phone")}
              </p>
              <Link
                to="/download"
                style={{ fontSize: 11.5, fontWeight: 700, color: "#34D399", marginTop: 4, display: "inline-block" }}
                data-testid="app-download-banner-link"
              >
                {tr("downloadBannerCta", "Or open download page →")}
              </Link>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label={tr("close", "Close")}
              className="self-start"
              style={{ color: "#64748B", background: "transparent", border: 0, cursor: "pointer", padding: 2 }}
              data-testid="app-download-banner-close"
            >
              <X size={15} strokeWidth={2.4} />
            </button>
          </motion.div>

          {/* Mobile — slim pill */}
          <motion.div
            key="app-banner-mobile"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }}
            className="flex lg:hidden fixed bottom-4 left-1/2 z-[60] items-center gap-2"
            style={{
              transform: "translateX(-50%)",
              padding: "9px 12px 9px 14px",
              borderRadius: 999,
              background: "rgba(11,18,32,0.9)",
              border: "1px solid rgba(52,211,153,0.28)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              boxShadow: "0 12px 30px -10px rgba(0,0,0,0.7)",
            }}
            data-testid="app-download-banner-mobile"
          >
            <Smartphone size={15} style={{ color: "#34D399" }} strokeWidth={2.4} />
            <Link
              to="/download"
              style={{ fontSize: 13, fontWeight: 700, color: "#F8FAFC" }}
              data-testid="app-download-banner-mobile-link"
            >
              {tr("downloadBannerTitle", "Get the app")}
            </Link>
            <button
              type="button"
              onClick={dismiss}
              aria-label={tr("close", "Close")}
              style={{ color: "#64748B", background: "transparent", border: 0, cursor: "pointer", padding: 2, marginLeft: 2 }}
              data-testid="app-download-banner-mobile-close"
            >
              <X size={15} strokeWidth={2.4} />
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AppDownloadBanner;
